/* eslint-disable @typescript-eslint/ban-ts-comment */
import { SearchClient } from 'algoliasearch';
import { liteClient as algoliasearch, SearchQuery } from 'algoliasearch/lite';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_APP_KEY,
  ALGOLIA_INDEX_EVENTS_SEARCH,
  AnalyticsTag,
  deviceAnalyticsTags
} from 'constants/algolia';
import {
  createNullCache,
  createBrowserLocalStorageCache,
  createMemoryCache,
  createFallbackableCache
} from '@algolia/client-common';
import { cookiesIdHandler } from 'middleware/cookieMiddlewareIdHandler';
import aa from 'search-insights';
import { isMobile as getIsMobile } from 'utils/misc';

if (!ALGOLIA_APP_ID || !ALGOLIA_APP_KEY || !ALGOLIA_INDEX_EVENTS_SEARCH) {
  throw new Error(
    'Algolia Application ID, API Key and Index must be defined in .env'
  );
}

interface Params {
  query?: string;
  numericFilters?: string[];
  highlightPostTag?: string;
  highlightPreTag?: string;
}

type CustomSearchQuery = SearchQuery & {
  params?: Params;
};

const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_APP_KEY, {
  requestsCache: createMemoryCache({ serializable: false }),
  responsesCache: createFallbackableCache({
    caches: [
      createBrowserLocalStorageCache({
        key: 'algolia-responses',
        timeToLive: 600
      }),
      createMemoryCache(),
      createNullCache()
    ]
  })
});

const searchClient: SearchClient = {
  ...algoliaClient,
  // @ts-ignore
  search(requests: CustomSearchQuery[]) {
    // TODO: check the reason for two requests with the difference between highlightPostTag and highlightPreTag
    requests.forEach((request) => {
      delete request?.params?.highlightPostTag;
      delete request?.params?.highlightPreTag;
    });

    const shouldBlockRequest = (request: CustomSearchQuery) => {
      if (request.indexName !== ALGOLIA_INDEX_EVENTS_SEARCH) {
        return false;
      }

      const query = request.params?.query || '';
      const dateFilters = request.params?.numericFilters;

      if (query === '' && (!dateFilters || dateFilters.length === 0))
        return true;

      return false;
    };

    const requestsToExecute = requests.filter(
      (req) => !shouldBlockRequest(req)
    );

    if (requestsToExecute.length === 0) {
      return Promise.resolve({
        results: requests.map((req) => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: req.params?.query || '',
          params: ''
        }))
      });
    }

    return algoliaClient.search(requestsToExecute).then((response) => {
      let executedIndex = 0;

      const results = requests.map((request) => {
        if (shouldBlockRequest(request)) {
          return {
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: ''
          };
        } else {
          return response.results[executedIndex++];
        }
      });

      return { results };
    });
  }
};

export default searchClient;

let sessionId = cookiesIdHandler().getSessionId();
if (!sessionId) sessionId = cookiesIdHandler().createSessionId();

const initInsightsAlgolia = () => {
  aa('init', {
    appId: ALGOLIA_APP_ID,
    apiKey: ALGOLIA_APP_KEY
  });

  aa('setUserToken', sessionId);
};

initInsightsAlgolia();
export const userTokenAlgolia = sessionId;

export const setAuthenticatedUserTokenAlgolia = () => {
  aa('setAuthenticatedUserToken', sessionId);
};

export const createAnalyticsTags = ({
  groupTag,
  componentTag,
  otherTags = []
}: {
  groupTag: AnalyticsTag;
  componentTag?: AnalyticsTag;
  otherTags?: AnalyticsTag[];
}): AnalyticsTag[] => {
  let deviceTag: AnalyticsTag = deviceAnalyticsTags.webMobile;
  const isMobile = getIsMobile();

  if (isMobile) {
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent;

      if (/android/i.test(ua)) {
        deviceTag = deviceAnalyticsTags.webMobileAndroid;
      } else if (/iPhone|iPad|iPod/i.test(ua)) {
        deviceTag = deviceAnalyticsTags.webMobileIOS;
      }
    }
  } else {
    deviceTag = deviceAnalyticsTags.webDesktop;
  }
  return [groupTag, componentTag, deviceTag, ...otherTags].filter(
    Boolean
  ) as AnalyticsTag[];
};
